import { openInMacAppStore } from '../../../../util/mac-app-store';
import { Component } from '@angular/core';
import { STORE_APPS, storeAppListPrice } from 'src/app/data/product/product-lookup.data';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';
import { CartPopupService } from 'src/app/service/cart/cart-popup.service';
import { StoreService } from 'src/app/service/store/store.service';
import { PRODUCT_HEROES } from '../product-hero.data';
import { ProductShots, Shot } from '../product-shots';

// Promo landing page for the Color Picker app for macOS, served at
// /plugins/macos/color-picker. This URL is also the app's marketing URL in App
// Store Connect, so it has to stay live and stay accurate. SEO is applied by the
// NavigationInterceptor from navigation.data.ts (client) and seo-route-meta.js
// (server). The copy describes only what the app actually does. Reuses the
// Chrome picker page styles, like the JetBrains page does.
@Component( {
  selector: 'app-macos-color-picker-page',
  templateUrl: './macos-color-picker-page.component.html',
  styleUrls: [ '../chrome-color-picker-page/chrome-color-picker-page.component.scss', '../macos-tidy-desktop-page/macos-tidy-desktop-page.component.scss', '../product-page.scss', './macos-color-picker-page.component.scss' ],
} )
export class MacosColorPickerPageComponent extends ProductShots {

  // Live on the Mac App Store since 2026-08-15, so every CTA is a real store
  // link. Kept as a flag because the page shipped before the listing was public.
  readonly released = true;
  readonly appStoreUrl = 'https://apps.apple.com/app/codigrate-color-picker/id6798887660';
  // Sold on the site too (2026-10-14), at the App Store's price: the notarized Developer ID build through the
  // cart. Until the store sells the sku (its catalogue entry is active with a Stripe price), the page stays as it
  // was, with the App Store as the only button.
  readonly storeSku = 'color-picker/macos';
  // The product on its stage at the top of the page (product-hero.data.ts), and the facts as
  // chips under the pitch.
  readonly hero = PRODUCT_HEROES[ 'macos-color-picker' ];
  readonly facts = [ 'Native Menu Bar App', 'macOS 13 Or Later', 'Apple Silicon' ];


  readonly features = [
    {
      title: 'Eyedrop Anywhere',
      description: 'Sample any pixel on your screen with the native macOS loupe. A design tool, a photo, another app, anything you can see.',
    },
    {
      title: 'Every Color Space',
      description: 'Hex, RGB, HSL, HSV, HWB, OKLCH, OKLab, CIELAB, CIELCH, CMYK, XYZ and the nearest RAL Classic, plus copy-ready CSS. Click a row to copy it.',
    },
    {
      title: 'Accessibility Checks',
      description: 'WCAG 2 contrast ratios with AA and AAA verdicts, and APCA Lc values, against white, black and the complementary color in both directions.',
    },
    {
      title: 'Harmony, Tints and Tones',
      description: 'Every classic scheme from the color wheel, with eleven step ramps toward white, black and gray, and a warm or cool verdict.',
    },
    {
      title: 'Color Vision Deficiency',
      description: 'Eight simulations covering protan, deutan, tritan and monochromacy vision, at mild and full strength.',
    },
    {
      title: 'Stays Out of the Way',
      description: 'It lives in the menu bar, not in another window, and the panel stays open while you sample so you can compare colors without losing your place.',
    },
  ];

  // The first three things it does under the product's picture, the rest further down (the
  // Apps & Plugins layout, ../product-page.scss).
  readonly highlights = this.features.slice( 0, 3 );
  readonly more = this.features.slice( 3 );

  // The store pictures (2026-10-09, in All In One Themes' store style), under Screenshots; each
  // opens in the lightbox at full size.
  readonly shots: Shot[] = [
    {
      name: 'Grab Any Color',
      src: './assets/product-lines/tools/macos-color-picker/screenshot-grab-any-color.webp',
      large: './assets/product-lines/tools/macos-color-picker/screenshot-grab-any-color-large.webp',
      alt: 'Grab any color, right on your Mac: the Color Picker panel from the menu bar with a picked indigo in every color space',
    },
    {
      name: 'Right Where You Work',
      src: './assets/product-lines/tools/macos-color-picker/screenshot-where-you-work.webp',
      large: './assets/product-lines/tools/macos-color-picker/screenshot-where-you-work-large.webp',
      alt: 'The Color Picker panel open from the menu bar over a photo of a tangerine in Preview, with its orange picked',
    },
  ];

  constructor(
    private analyticsService: AnalyticsService,
    private cartPopupService: CartPopupService,
    private storeService: StoreService,
  ) {
    super();
  }

  /** the store sells the app on the site: the cart is the first button and the App Store the second */
  get sellsHere(): boolean {
    return !!STORE_APPS[ this.storeSku ] && this.storeService.availability( this.storeSku ) === 'buy';
  }

  /** "$2.99": the live Stripe price, else the list price */
  get priceLabel(): string | null {
    return this.storeService.priceLabel( this.storeSku, storeAppListPrice( this.storeSku ) );
  }

  /** puts the app in the cart and opens the mini cart (a toast on a phone); the page stays */
  addToCart(): void {
    this.cartPopupService.add( this.storeSku, 'macos-color-picker-page' );
  }

  onInstall( event?: Event ): void {
    this.analyticsService.logEvent( 'App Install Clicked', { app: 'macos-color-picker' } );
    // on a Mac the App Store app opens at once (any browser); elsewhere the web listing
    openInMacAppStore( event, this.appStoreUrl );
  }
}
